import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CollapsibleSection,
  PastCompletedOrdersSection,
  AnusthanScheduleSection,
  BhojDaanScheduleSection,
  AstroAnalysisUploadSection
} from './SharedComponents';
import { BraceletConfigSection, RecommendAnusthanSection } from './BraceletAndRecommendComponents';
import { OrderExpandedView } from './OrderExpandedView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ══════════════════════════════════════════
// NEW ORDERS TAB COMPONENT
// ══════════════════════════════════════════

export const NewOrdersTab = ({ orders, anusthans, onRefresh, onUploadAnalysis, onRemoveAnalysis, onUpdateAstroStatus, onSaveBraceletConfig, onSaveRecommendations, onUpdateStatus, onScheduleAnusthan, onScheduleBhojDaan }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const thStyle = { 
    padding: '14px 16px', 
    textAlign: 'left', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.7rem', 
    letterSpacing: '0.1em', 
    textTransform: 'uppercase', 
    color: '#725b47', 
    fontWeight: '600',
    background: '#f5efe6'
  };
  
  const tdStyle = { 
    padding: '14px 16px', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.85rem', 
    color: '#2E221A', 
    verticalAlign: 'middle' 
  };

  // Sort orders by date ascending (oldest first, latest last)
  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateA - dateB;
  });

  // Filter orders based on search term
  const filteredOrders = sortedOrders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (order.customer_id || '').toLowerCase().includes(search) ||
      (order.customer_name || '').toLowerCase().includes(search) ||
      (order.customer_phone || '').toLowerCase().includes(search) ||
      (order.customer_email || '').toLowerCase().includes(search) ||
      (order.order_id || '').toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when search or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceSubtype = (order) => {
    if (order.service_type === 'astro_analysis') return 'With Bracelet';
    if (order.service_type === 'anusthan') return order.anusthan_name || order.package_name || '-';
    if (order.service_type === 'bhoj_daan') {
      const subtypes = order.sub_service_types || [];
      if (subtypes.length > 0) {
        return subtypes.length > 2 ? `${subtypes.slice(0, 2).join(', ')}...` : subtypes.join(', ');
      }
      return order.package_name || '-';
    }
    return '-';
  };

  const getServiceDisplayName = (serviceType) => {
    switch (serviceType) {
      case 'astro_analysis': return 'Astro Analysis';
      case 'anusthan': return 'Anusthan';
      case 'bhoj_daan': return 'Bhoj & Daan';
      default: return serviceType || 'Unknown';
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d5c5' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', background: '#f5efe6', borderBottom: '1px solid #e0d5c5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#2E221A', margin: 0 }}>
              New Orders
            </h3>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#725b47', margin: '8px 0 0' }}>
              Customer orders requiring action ({filteredOrders.length} of {orders.length})
            </p>
          </div>
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: isRefreshing ? '#f5f5f5' : 'transparent', color: '#8B4513', border: '1px solid #8B4513', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
          >
            {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
        
        {/* Search and Pagination Controls */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search by Customer ID, Name, Mobile, Email, Order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.9rem',
                border: '1px solid #e0d5c5',
                background: '#fff',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#725b47' }}>Show:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', border: '1px solid #e0d5c5', cursor: 'pointer' }}
            >
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={100}>100</option>
            </select>
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#725b47' }}>per page</span>
          </div>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>{searchTerm ? '🔍' : '✅'}</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: searchTerm ? '#725b47' : '#2E7D32', marginBottom: '8px' }}>
            {searchTerm ? 'No Results Found' : 'All Clear!'}
          </div>
          <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#725b47' }}>
            {searchTerm ? `No orders matching "${searchTerm}"` : 'No new orders at this time.'}
          </div>
        </div>
      ) : (
        <>
          <div className="admin-table-container" style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr>
                <th style={thStyle}>S.No</th>
                <th style={thStyle}>Customer ID</th>
                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Order Date/Time</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle} className="hide-on-mobile">Service Subtype</th>
                <th style={thStyle} className="hide-on-mobile">Package / Price</th>
                <th style={thStyle}>Mobile</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order, idx) => (
                <React.Fragment key={order.order_id}>
                  <tr 
                  style={{ 
                    borderBottom: '1px solid #f0e8dc', 
                    cursor: 'pointer',
                    background: expandedOrder === order.order_id ? '#faf8f5' : 'transparent'
                  }}
                  onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                >
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#8B4513', fontWeight: '600' }}>
                      {order.customer_id}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span>
                  </td>
                  <td style={tdStyle}>{formatDateTime(order.created_at)}</td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: '500', color: '#8B4513' }}>
                      {getServiceDisplayName(order.service_type)}
                    </span>
                  </td>
                  <td style={tdStyle}>{getServiceSubtype(order)}</td>
                  <td style={tdStyle}>
                    {order.service_type === 'anusthan' && order.package_name ? (
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.8rem', color: '#2E221A' }}>{order.package_name}</div>
                        {order.package_price > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#2E7D32', fontWeight: '600' }}>₹{order.package_price}</div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>-</span>
                    )}
                  </td>
                  <td style={tdStyle}>{order.customer_phone || '-'}</td>
                  <td style={tdStyle}>
                    <span style={{ 
                      padding: '4px 12px', 
                      fontSize: '0.7rem', 
                      fontWeight: '600',
                      background: 'rgba(255,152,0,0.15)',
                      color: '#E65100'
                    }}>
                      NEW
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id); }}
                      style={{ 
                        padding: '8px 16px', 
                        fontFamily: 'Lato, sans-serif', 
                        fontSize: '0.75rem', 
                        background: '#8B4513', 
                        color: '#fff', 
                        border: 'none', 
                        cursor: 'pointer' 
                      }}
                    >
                      {expandedOrder === order.order_id ? '▲ Collapse' : '▼ Expand'}
                    </button>
                  </td>
                </tr>
                
                {/* Expanded Content */}
                {expandedOrder === order.order_id && (
                  <tr>
                    <td colSpan="10" style={{ padding: 0, background: '#faf8f5' }}>
                      <OrderExpandedView 
                        order={order}
                        anusthans={anusthans}
                        onUploadAnalysis={onUploadAnalysis}
                        onRemoveAnalysis={onRemoveAnalysis}
                        onUpdateAstroStatus={onUpdateAstroStatus}
                        onSaveBraceletConfig={onSaveBraceletConfig}
                        onSaveRecommendations={onSaveRecommendations}
                        onUpdateStatus={onUpdateStatus}
                        onScheduleAnusthan={onScheduleAnusthan}
                        onScheduleBhojDaan={onScheduleBhojDaan}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="admin-pagination" style={{ padding: '16px 24px', background: '#f5efe6', borderTop: '1px solid #e0d5c5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div className="admin-pagination-info" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#725b47' }}>
              Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            <div className="admin-pagination-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="admin-pagination-btn"
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === 1 ? '#e0d5c5' : '#8B4513', color: currentPage === 1 ? '#999' : '#fff', border: 'none', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="admin-pagination-btn"
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === 1 ? '#e0d5c5' : '#8B4513', color: currentPage === 1 ? '#999' : '#fff', border: 'none', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                ← Prev
              </button>
              <span style={{ padding: '8px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#2E221A' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="admin-pagination-btn"
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === totalPages ? '#e0d5c5' : '#8B4513', color: currentPage === totalPages ? '#999' : '#fff', border: 'none', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next →
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="admin-pagination-btn"
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === totalPages ? '#e0d5c5' : '#8B4513', color: currentPage === totalPages ? '#999' : '#fff', border: 'none', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Last
              </button>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// IN PROGRESS ASTRO ANALYSIS ORDERS TAB
// ══════════════════════════════════════════

export const InProgressOrdersTab = ({ orders, anusthans, onRefresh, onUploadAnalysis, onRemoveAnalysis, onUpdateAstroStatus, onSaveBraceletConfig, onSaveRecommendations, onUpdateStatus }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get display status for an order
  const getOrderStatus = (order) => {
    const astroStatus = order.astro_analysis_status;
    const braceletStatus = order.bracelet_status;
    
    if (braceletStatus && braceletStatus !== 'not_started') {
      return braceletStatus;
    }
    if (astroStatus === 'done') {
      return 'astro_done';
    }
    return 'in_progress';
  };

  const thStyle = { 
    padding: '14px 16px', 
    textAlign: 'left', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.7rem', 
    letterSpacing: '0.1em', 
    textTransform: 'uppercase', 
    color: '#725b47', 
    fontWeight: '600',
    background: '#e3f2fd'
  };
  
  const tdStyle = { 
    padding: '14px 16px', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.85rem', 
    color: '#2E221A', 
    verticalAlign: 'middle' 
  };

  // Sort orders by date ascending (oldest first, latest last)
  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateA - dateB;
  });

  // Filter orders based on search and status
  const filteredOrders = sortedOrders.filter(order => {
    // Status filter
    if (statusFilter !== 'all') {
      const orderStatus = getOrderStatus(order);
      if (statusFilter !== orderStatus) return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (order.customer_id || '').toLowerCase().includes(search) ||
      (order.customer_name || '').toLowerCase().includes(search) ||
      (order.customer_phone || '').toLowerCase().includes(search) ||
      (order.customer_email || '').toLowerCase().includes(search) ||
      (order.order_id || '').toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + 
           date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #bbdefb' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #bbdefb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e3f2fd' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#1565C0', margin: 0 }}>In Progress Astro Analysis Orders</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#1976D2', margin: '4px 0 0' }}>
            Orders with Astro Analysis in progress ({filteredOrders.length} of {orders.length})
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#90CAF9' : '#1565C0', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      
      {orders.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📄</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#1565C0', marginBottom: '8px' }}>No Orders In Progress</div>
          <div style={{ fontFamily: 'Lato, sans-serif', color: '#1976D2' }}>Orders will appear here when Astro Analysis work begins</div>
        </div>
      ) : (
        <>
        {/* Search and Pagination Controls */}
        <div style={{ padding: '16px 24px', background: '#f5f5f5', borderBottom: '1px solid #bbdefb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <input
            type="text"
            placeholder="Search by Customer ID, Name, Mobile, Email, Order ID..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={{ flex: '1', minWidth: '250px', padding: '10px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', border: '1px solid #bbdefb', background: '#fff' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#725b47' }}>Status:</span>
            <select 
              value={statusFilter} 
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', border: '1px solid #1565C0', background: '#fff', minWidth: '180px' }}
            >
              <option value="all">All Statuses</option>
              <option value="in_progress">Astro Analysis in Progress</option>
              <option value="astro_done">Astro Analysis is Done</option>
              <option value="configured">Bracelet is Configured</option>
              <option value="ready_to_ship">Bracelet Ready to Ship</option>
              <option value="shipped">Bracelet is Shipped</option>
              <option value="delivered">Bracelet is Delivered</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#725b47' }}>Show:</span>
            <select 
              value={pageSize} 
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', border: '1px solid #bbdefb' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#725b47' }}>per page</span>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>S.No</th>
              <th style={thStyle}>Customer ID</th>
              <th style={thStyle}>Order ID</th>
              <th style={thStyle}>Order Date/Time</th>
              <th style={thStyle}>Customer Name</th>
              <th style={thStyle}>Mobile</th>
              <th style={thStyle}>Package / Price</th>
              <th style={thStyle}>Analysis Status</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order, index) => (
              <React.Fragment key={order.order_id}>
                <tr 
                  style={{ 
                    borderBottom: '1px solid #bbdefb', 
                    cursor: 'pointer',
                    background: expandedOrder === order.order_id ? '#e3f2fd' : 'transparent'
                  }}
                  onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                >
                  <td style={tdStyle}>{startIndex + index + 1}</td>
                  <td style={tdStyle}>
                    <span style={{ color: '#1565C0', fontWeight: '500' }}>{order.customer_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span>
                  </td>
                  <td style={tdStyle}>{formatDate(order.created_at)}</td>
                  <td style={tdStyle}>{order.customer_name}</td>
                  <td style={tdStyle}>{order.customer_phone}</td>
                  <td style={tdStyle}>
                    {order.package_name ? (
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.8rem', color: '#2E221A' }}>{order.package_name}</div>
                        {order.package_price > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#2E7D32', fontWeight: '600' }}>₹{order.package_price}</div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>-</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {(() => {
                      // Determine the overall status to display
                      const astroStatus = order.astro_analysis_status;
                      const braceletStatus = order.bracelet_status;
                      
                      // If bracelet has a status beyond "not_started", show bracelet status
                      if (braceletStatus && braceletStatus !== 'not_started') {
                        switch (braceletStatus) {
                          case 'configured':
                            return (
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(33,150,243,0.15)', color: '#1565C0' }}>
                                Bracelet is Configured
                              </span>
                            );
                          case 'ready_to_ship':
                            return (
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(156,39,176,0.15)', color: '#7B1FA2' }}>
                                Bracelet is Ready to Ship
                              </span>
                            );
                          case 'shipped':
                            return (
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(0,150,136,0.15)', color: '#00838F' }}>
                                Bracelet is Shipped
                              </span>
                            );
                          case 'delivered':
                            return (
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(76,175,80,0.15)', color: '#2E7D32' }}>
                                Bracelet is Delivered
                              </span>
                            );
                          default:
                            break;
                        }
                      }
                      
                      // If astro analysis is done, show that status
                      if (astroStatus === 'done') {
                        return (
                          <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(76,175,80,0.15)', color: '#2E7D32' }}>
                            Astro Analysis is Done
                          </span>
                        );
                      }
                      
                      // Default: In Progress
                      return (
                        <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(33,150,243,0.15)', color: '#1565C0' }}>
                          Astro Analysis in Progress
                        </span>
                      );
                    })()}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id); }}
                      style={{ 
                        padding: '8px 16px', 
                        fontFamily: 'Lato, sans-serif', 
                        fontSize: '0.8rem', 
                        background: expandedOrder === order.order_id ? '#1565C0' : '#1976D2', 
                        color: '#fff', 
                        border: 'none', 
                        cursor: 'pointer' 
                      }}
                    >
                      {expandedOrder === order.order_id ? '▲ Collapse' : '▼ Expand'}
                    </button>
                  </td>
                </tr>
                
                {/* Expanded Content */}
                {expandedOrder === order.order_id && (
                  <tr>
                    <td colSpan="9" style={{ padding: 0, background: '#e3f2fd' }}>
                      <OrderExpandedView 
                        order={order}
                        anusthans={anusthans}
                        onUploadAnalysis={onUploadAnalysis}
                        onRemoveAnalysis={onRemoveAnalysis}
                        onUpdateAstroStatus={onUpdateAstroStatus}
                        onSaveBraceletConfig={onSaveBraceletConfig}
                        onSaveRecommendations={onSaveRecommendations}
                        onUpdateStatus={onUpdateStatus}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ padding: '16px 24px', background: '#e3f2fd', borderTop: '1px solid #bbdefb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#1976D2' }}>
              Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredOrders.length)} of {filteredOrders.length} orders
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === 1 ? '#bbdefb' : '#1565C0', color: currentPage === 1 ? '#999' : '#fff', border: 'none', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === 1 ? '#bbdefb' : '#1565C0', color: currentPage === 1 ? '#999' : '#fff', border: 'none', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                Prev
              </button>
              <span style={{ padding: '8px 16px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#1565C0' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === totalPages ? '#bbdefb' : '#1565C0', color: currentPage === totalPages ? '#999' : '#fff', border: 'none', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{ padding: '8px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: currentPage === totalPages ? '#bbdefb' : '#1565C0', color: currentPage === totalPages ? '#999' : '#fff', border: 'none', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Last
              </button>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// SCHEDULED ANUSTHANS TAB COMPONENT
// ══════════════════════════════════════════

export const ScheduledAnusthansTab = ({ groups, onRefresh, onSendVideoUrl }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [videoUrl, setVideoUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelectAll = (groupKey, customers) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.length === customers.length) {
      // Deselect all
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
    } else {
      // Select all
      setSelectedOrders({ ...selectedOrders, [groupKey]: customers.map(c => c.order_id) });
    }
  };

  const handleSelectOrder = (groupKey, orderId) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.includes(orderId)) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: currentSelected.filter(id => id !== orderId) });
    } else {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [...currentSelected, orderId] });
    }
  };

  const handleSendVideoUrl = async (groupKey) => {
    const orderIds = selectedOrders[groupKey] || [];
    if (orderIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    
    setSending(true);
    const success = await onSendVideoUrl(orderIds, videoUrl.trim());
    if (success) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
      setVideoUrl('');
    }
    setSending(false);
  };

  const thStyle = { 
    padding: '12px 14px', 
    textAlign: 'left', 
    fontSize: '0.7rem', 
    color: '#4A148C', 
    textTransform: 'uppercase', 
    borderBottom: '1px solid #ce93d8',
    fontWeight: '600',
    background: '#f3e5f5'
  };

  const tdStyle = { 
    padding: '12px 14px', 
    fontSize: '0.85rem', 
    verticalAlign: 'middle' 
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #ce93d8' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #ce93d8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f3e5f5' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#4A148C', margin: 0 }}>Scheduled Anusthans</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#7B1FA2', margin: '4px 0 0' }}>
            Anusthans grouped by ceremony date ({groups.length} groups)
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#BA68C8' : '#7B1FA2', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      
      {groups.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📅</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#7B1FA2', marginBottom: '8px' }}>No Scheduled Anusthans</div>
          <div style={{ fontFamily: 'Lato, sans-serif', color: '#9c27b0' }}>Anusthans will appear here when scheduled from New Orders</div>
        </div>
      ) : (
        <div style={{ padding: '20px' }}>
          {groups.map((group, idx) => {
            const groupKey = `${group.anusthan_name}||${group.ceremony_date}`;
            const groupSelectedOrders = selectedOrders[groupKey] || [];
            
            return (
              <div key={idx} style={{ marginBottom: '20px', border: '1px solid #ce93d8', background: '#fff' }}>
                {/* Group Header */}
                <div 
                  onClick={() => setExpandedGroup(expandedGroup === groupKey ? null : groupKey)}
                  style={{ 
                    padding: '16px 20px', 
                    background: '#f3e5f5', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#4A148C' }}>{group.anusthan_name}</span>
                    <span style={{ fontSize: '0.9rem', color: '#7B1FA2' }}>📅 {group.ceremony_date}</span>
                    <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(123,31,162,0.15)', color: '#7B1FA2' }}>
                      {group.customer_count} Customer(s)
                    </span>
                    <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(123,31,162,0.25)', color: '#4A148C' }}>
                      Scheduled
                    </span>
                  </div>
                  <span style={{ 
                    fontSize: '1.2rem', 
                    color: '#7B1FA2',
                    transition: 'transform 0.2s',
                    transform: expandedGroup === groupKey ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </div>
                
                {/* Expanded Group Content */}
                {expandedGroup === groupKey && (
                  <div style={{ padding: '20px' }}>
                    {/* Customer Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: '40px' }}>
                            <input 
                              type="checkbox"
                              checked={groupSelectedOrders.length === group.customers.length}
                              onChange={() => handleSelectAll(groupKey, group.customers)}
                              style={{ cursor: 'pointer' }}
                            />
                          </th>
                          <th style={thStyle}>Customer ID</th>
                          <th style={thStyle}>Order ID</th>
                          <th style={thStyle}>Order Date/Time</th>
                          <th style={thStyle}>Package Name</th>
                          <th style={thStyle}>Price</th>
                          <th style={thStyle}>Comments</th>
                          <th style={thStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.customers.map((customer, cIdx) => (
                          <tr key={customer.order_id} style={{ borderBottom: '1px solid #e1bee7', background: cIdx % 2 === 0 ? '#fff' : '#faf5fc' }}>
                            <td style={tdStyle}>
                              <input 
                                type="checkbox"
                                checked={groupSelectedOrders.includes(customer.order_id)}
                                onChange={() => handleSelectOrder(groupKey, customer.order_id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={tdStyle}>
                              <span style={{ color: '#7B1FA2', fontWeight: '500' }}>{customer.customer_id}</span>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{customer.order_id}</span>
                            </td>
                            <td style={tdStyle}>{formatDateTime(customer.created_at)}</td>
                            <td style={tdStyle}>{customer.package_name || '-'}</td>
                            <td style={tdStyle}>{customer.package_price > 0 ? `₹${customer.package_price}` : '-'}</td>
                            <td style={{ ...tdStyle, maxWidth: '200px' }}>
                              {customer.comments ? (
                                <span style={{ 
                                  display: 'block',
                                  fontSize: '0.8rem', 
                                  color: '#4A148C',
                                  background: 'rgba(212,175,55,0.15)',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  wordBreak: 'break-word'
                                }}>
                                  📝 {customer.comments}
                                </span>
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.8rem' }}>-</span>
                              )}
                            </td>
                            <td style={tdStyle}>
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(123,31,162,0.15)', color: '#7B1FA2' }}>
                                Scheduled
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Video URL Section */}
                    <div style={{ padding: '20px', background: '#f3e5f5', border: '1px solid #ce93d8' }}>
                      <h4 style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#4A148C', marginBottom: '16px' }}>
                        📹 Send Video URL to Selected Customers
                      </h4>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#7B1FA2', marginBottom: '8px' }}>
                            YouTube / Instagram / Facebook Live URL
                          </label>
                          <input
                            type="text"
                            placeholder="https://youtube.com/watch?v=... or https://instagram.com/..."
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              fontFamily: 'Lato, sans-serif', 
                              fontSize: '0.9rem', 
                              border: '1px solid #ce93d8', 
                              background: '#fff' 
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleSendVideoUrl(groupKey)}
                          disabled={sending || groupSelectedOrders.length === 0}
                          style={{
                            padding: '12px 24px',
                            fontFamily: 'Cinzel, serif',
                            fontSize: '0.9rem',
                            background: sending || groupSelectedOrders.length === 0 ? '#ccc' : '#7B1FA2',
                            color: '#fff',
                            border: 'none',
                            cursor: sending || groupSelectedOrders.length === 0 ? 'not-allowed' : 'pointer',
                            letterSpacing: '0.05em'
                          }}
                        >
                          {sending ? 'Sending...' : `📤 Send to ${groupSelectedOrders.length} Customer(s)`}
                        </button>
                      </div>
                      <p style={{ marginTop: '12px', fontSize: '0.8rem', color: '#9c27b0' }}>
                        Selected customers will receive the video URL via WhatsApp and their orders will be marked as "Completed".
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// SCHEDULED BHOJ & DAAN TAB COMPONENT
// ══════════════════════════════════════════

export const ScheduledBhojDaanTab = ({ groups, onRefresh, onSendVideoUrl }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [videoUrl, setVideoUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelectAll = (groupKey, customers) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.length === customers.length) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
    } else {
      setSelectedOrders({ ...selectedOrders, [groupKey]: customers.map(c => c.order_id) });
    }
  };

  const handleSelectOrder = (groupKey, orderId) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.includes(orderId)) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: currentSelected.filter(id => id !== orderId) });
    } else {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [...currentSelected, orderId] });
    }
  };

  const handleSendVideoUrl = async (groupKey) => {
    const orderIds = selectedOrders[groupKey] || [];
    if (orderIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }
    
    setSending(true);
    const success = await onSendVideoUrl(orderIds, videoUrl.trim());
    if (success) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
      setVideoUrl('');
    }
    setSending(false);
  };

  const thStyle = { 
    padding: '12px 14px', 
    textAlign: 'left', 
    fontSize: '0.7rem', 
    color: '#004D40', 
    textTransform: 'uppercase', 
    borderBottom: '1px solid #80cbc4',
    fontWeight: '600',
    background: '#e0f2f1'
  };

  const tdStyle = { 
    padding: '12px 14px', 
    fontSize: '0.85rem', 
    verticalAlign: 'middle' 
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #80cbc4' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #80cbc4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e0f2f1' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#004D40', margin: 0 }}>Scheduled Bhoj & Daan</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#00695C', margin: '4px 0 0' }}>
            Donations grouped by seva type and ceremony date ({groups.length} groups)
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#4DB6AC' : '#00695C', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      
      {groups.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🙏</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#00695C', marginBottom: '8px' }}>No Scheduled Bhoj & Daan</div>
          <div style={{ fontFamily: 'Lato, sans-serif', color: '#009688' }}>Donations will appear here when scheduled from New Orders</div>
        </div>
      ) : (
        <div style={{ padding: '20px' }}>
          {groups.map((group, idx) => {
            const groupKey = `${group.donation_type}||${group.ceremony_date}`;
            const groupSelectedOrders = selectedOrders[groupKey] || [];
            
            return (
              <div key={idx} style={{ marginBottom: '20px', border: '1px solid #80cbc4', background: '#fff' }}>
                {/* Group Header */}
                <div 
                  onClick={() => setExpandedGroup(expandedGroup === groupKey ? null : groupKey)}
                  style={{ 
                    padding: '16px 20px', 
                    background: '#e0f2f1', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#004D40' }}>{group.donation_type}</span>
                    <span style={{ fontSize: '0.9rem', color: '#00695C' }}>📅 {group.ceremony_date}</span>
                    <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(0,105,92,0.15)', color: '#00695C' }}>
                      {group.customer_count} Customer(s)
                    </span>
                    <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(0,105,92,0.15)', color: '#00695C' }}>
                      {group.total_people} People
                    </span>
                    <span style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(0,105,92,0.25)', color: '#004D40' }}>
                      ₹{(group.total_amount || 0).toLocaleString()}
                    </span>
                  </div>
                  <span style={{ 
                    fontSize: '1.2rem', 
                    color: '#00695C',
                    transition: 'transform 0.2s',
                    transform: expandedGroup === groupKey ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </div>
                
                {/* Expanded Group Content */}
                {expandedGroup === groupKey && (
                  <div style={{ padding: '20px' }}>
                    {/* Customer Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, width: '40px' }}>
                            <input 
                              type="checkbox"
                              checked={groupSelectedOrders.length === group.customers.length}
                              onChange={() => handleSelectAll(groupKey, group.customers)}
                              style={{ cursor: 'pointer' }}
                            />
                          </th>
                          <th style={thStyle}>Customer ID</th>
                          <th style={thStyle}>Order ID</th>
                          <th style={thStyle}>Order Date/Time</th>
                          <th style={thStyle}>Package</th>
                          <th style={thStyle}>People</th>
                          <th style={thStyle}>Price</th>
                          <th style={thStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.customers.map((customer, cIdx) => (
                          <tr key={customer.order_id} style={{ borderBottom: '1px solid #b2dfdb', background: cIdx % 2 === 0 ? '#fff' : '#f5fcfb' }}>
                            <td style={tdStyle}>
                              <input 
                                type="checkbox"
                                checked={groupSelectedOrders.includes(customer.order_id)}
                                onChange={() => handleSelectOrder(groupKey, customer.order_id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={tdStyle}>
                              <span style={{ color: '#00695C', fontWeight: '500' }}>{customer.customer_id}</span>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{customer.order_id}</span>
                            </td>
                            <td style={tdStyle}>{formatDateTime(customer.created_at)}</td>
                            <td style={tdStyle}>{customer.package_name || '-'}</td>
                            <td style={tdStyle}>{customer.people || '-'}</td>
                            <td style={tdStyle}>{customer.package_price > 0 ? `₹${customer.package_price.toLocaleString()}` : '-'}</td>
                            <td style={tdStyle}>
                              <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', background: 'rgba(0,105,92,0.15)', color: '#00695C' }}>
                                Scheduled
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Video URL Section */}
                    <div style={{ padding: '20px', background: '#e0f2f1', border: '1px solid #80cbc4' }}>
                      <h4 style={{ fontFamily: 'Cinzel, serif', fontSize: '1rem', color: '#004D40', marginBottom: '16px' }}>
                        📹 Send Video URL to Selected Customers
                      </h4>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#00695C', marginBottom: '8px' }}>
                            YouTube / Instagram / Facebook Live URL
                          </label>
                          <input
                            type="text"
                            placeholder="https://youtube.com/watch?v=... or https://instagram.com/..."
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              fontFamily: 'Lato, sans-serif', 
                              fontSize: '0.9rem', 
                              border: '1px solid #80cbc4', 
                              background: '#fff' 
                            }}
                          />
                        </div>
                        <button
                          onClick={() => handleSendVideoUrl(groupKey)}
                          disabled={sending || groupSelectedOrders.length === 0}
                          style={{
                            padding: '12px 24px',
                            fontFamily: 'Cinzel, serif',
                            fontSize: '0.9rem',
                            background: sending || groupSelectedOrders.length === 0 ? '#ccc' : '#00695C',
                            color: '#fff',
                            border: 'none',
                            cursor: sending || groupSelectedOrders.length === 0 ? 'not-allowed' : 'pointer',
                            letterSpacing: '0.05em'
                          }}
                        >
                          {sending ? 'Sending...' : `📤 Send to ${groupSelectedOrders.length} Customer(s)`}
                        </button>
                      </div>
                      <p style={{ marginTop: '12px', fontSize: '0.8rem', color: '#009688' }}>
                        Selected customers will receive the video URL via WhatsApp and their orders will be marked as "Completed".
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// RECOMMENDED ANUSTHANS TAB COMPONENT
// ══════════════════════════════════════════

export const RecommendedAnusthansTab = ({ groups, onRefresh, onSendReminder }) => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [sending, setSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelectAll = (groupKey, customers) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.length === customers.length) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
    } else {
      setSelectedOrders({ ...selectedOrders, [groupKey]: customers.map(c => c.order_id) });
    }
  };

  const handleSelectOrder = (groupKey, orderId) => {
    const currentSelected = selectedOrders[groupKey] || [];
    if (currentSelected.includes(orderId)) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: currentSelected.filter(id => id !== orderId) });
    } else {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [...currentSelected, orderId] });
    }
  };

  const handleSendReminder = async (group) => {
    const groupKey = `${group.anusthan_id}||${group.ceremony_date}`;
    const orderIds = selectedOrders[groupKey] || [];
    
    if (orderIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    
    setSending(true);
    const success = await onSendReminder(orderIds, group.anusthan_name, group.ceremony_date);
    if (success) {
      setSelectedOrders({ ...selectedOrders, [groupKey]: [] });
    }
    setSending(false);
  };

  const thStyle = { 
    padding: '12px 14px', 
    textAlign: 'left', 
    fontSize: '0.7rem', 
    color: '#00695C', 
    textTransform: 'uppercase', 
    borderBottom: '1px solid #80CBC4',
    fontWeight: '600',
    background: '#E0F2F1'
  };

  const tdStyle = { 
    padding: '12px 14px', 
    fontSize: '0.85rem', 
    verticalAlign: 'middle' 
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #80CBC4' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #80CBC4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#E0F2F1' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#00695C', margin: 0 }}>Recommended Anusthans</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#00897B', margin: '4px 0 0' }}>
            Customers with pending recommendations ({groups.reduce((acc, g) => acc + g.customer_count, 0)} customers in {groups.length} groups)
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#4DB6AC' : '#00897B', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
      
      {groups.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🪔</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#00897B', marginBottom: '8px' }}>No Pending Recommendations</div>
          <div style={{ fontFamily: 'Lato, sans-serif', color: '#4DB6AC' }}>Customers who have been recommended Anusthans but haven't booked yet will appear here</div>
        </div>
      ) : (
        <div style={{ padding: '16px' }}>
          {groups.map((group, idx) => {
            const groupKey = `${group.anusthan_id}||${group.ceremony_date}`;
            const isExpanded = expandedGroup === groupKey;
            const selected = selectedOrders[groupKey] || [];
            
            return (
              <div key={idx} style={{ marginBottom: '12px', border: '1px solid #B2DFDB', borderRadius: '4px', overflow: 'hidden' }}>
                {/* Group Header */}
                <div 
                  onClick={() => setExpandedGroup(isExpanded ? null : groupKey)}
                  style={{ 
                    padding: '16px 20px', 
                    background: '#E0F2F1', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#00695C' }}>
                      {group.anusthan_name}
                    </span>
                    <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#00897B' }}>
                      📅 {group.display_date || group.ceremony_date || 'Date TBD'}
                    </span>
                    <span style={{ 
                      background: '#00897B', 
                      color: '#fff', 
                      padding: '4px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.75rem' 
                    }}>
                      {group.customer_count} Customer(s)
                    </span>
                    <span style={{ 
                      background: '#FF9800', 
                      color: '#fff', 
                      padding: '4px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.75rem' 
                    }}>
                      Pending
                    </span>
                  </div>
                  <span style={{ color: '#00695C', fontSize: '1.2rem' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ padding: '16px', background: '#fff' }}>
                    {/* Customer Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>
                            <input 
                              type="checkbox" 
                              checked={selected.length === group.customers.length && group.customers.length > 0}
                              onChange={() => handleSelectAll(groupKey, group.customers)}
                            />
                          </th>
                          <th style={thStyle}>Customer ID</th>
                          <th style={thStyle}>Order ID</th>
                          <th style={thStyle}>Order Date/Time</th>
                          <th style={thStyle}>Mobile Number</th>
                          <th style={thStyle}>Email ID</th>
                          <th style={thStyle}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.customers.map((customer, cIdx) => (
                          <tr key={cIdx} style={{ borderBottom: '1px solid #E0F2F1', background: selected.includes(customer.order_id) ? '#E8F5E9' : 'transparent' }}>
                            <td style={tdStyle}>
                              <input 
                                type="checkbox" 
                                checked={selected.includes(customer.order_id)}
                                onChange={() => handleSelectOrder(groupKey, customer.order_id)}
                              />
                            </td>
                            <td style={{ ...tdStyle, color: '#00897B', fontWeight: '500' }}>{customer.customer_id}</td>
                            <td style={tdStyle}>{customer.order_id}</td>
                            <td style={tdStyle}>{formatDateTime(customer.created_at)}</td>
                            <td style={tdStyle}>{customer.customer_phone}</td>
                            <td style={tdStyle}>{customer.customer_email}</td>
                            <td style={tdStyle}>
                              <span style={{ 
                                background: '#FF9800', 
                                color: '#fff', 
                                padding: '2px 8px', 
                                borderRadius: '4px', 
                                fontSize: '0.75rem' 
                              }}>
                                Not Booked
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Send Reminder Section */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: '#E8F5E9', borderRadius: '4px' }}>
                      <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#2E7D32' }}>
                        📱 Send WhatsApp Reminder ({selected.length} selected)
                      </span>
                      <button
                        onClick={() => handleSendReminder(group)}
                        disabled={sending || selected.length === 0}
                        style={{ 
                          padding: '10px 20px', 
                          background: selected.length > 0 ? '#2E7D32' : '#A5D6A7', 
                          color: '#fff', 
                          border: 'none', 
                          cursor: selected.length > 0 ? 'pointer' : 'not-allowed',
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '0.85rem'
                        }}
                      >
                        {sending ? '⏳ Sending...' : `📤 Send Reminder to ${selected.length} Customer(s)`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// AVAILABLE ANUSTHANS TAB COMPONENT
// ══════════════════════════════════════════

export const AvailableAnusthansTab = ({ anusthans, onRefresh, onCreate, onUpdate, onDuplicate, onDelete }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingAnusthan, setEditingAnusthan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed');
    } catch (err) {
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredAnusthans = anusthans.filter(a => {
    if (!showInactive && !a.is_active) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (a.name || '').toLowerCase().includes(search) ||
      (a.deity || '').toLowerCase().includes(search) ||
      (a.devName || '').toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #FFCCBC' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #FFCCBC', background: '#FBE9E7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#BF360C', margin: 0 }}>List of Available Anusthans</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#E64A19', margin: '4px 0 0' }}>
            Manage Anusthans displayed on the website ({filteredAnusthans.length} shown)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={() => { setShowCreateForm(true); setEditingAnusthan(null); }}
            style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: '#4CAF50', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            + Add New Anusthan
          </button>
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#FF8A65' : '#FF5722', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
          >
            {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div style={{ padding: '16px 24px', background: '#FFF3E0', borderBottom: '1px solid #FFCCBC', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, deity..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1', minWidth: '250px', padding: '10px 16px', border: '1px solid #FFCCBC', fontFamily: 'Lato, sans-serif' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666' }}>Show inactive</span>
        </label>
      </div>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingAnusthan) && (
        <AnusthanForm
          anusthan={editingAnusthan}
          onSave={async (data) => {
            if (editingAnusthan) {
              const success = await onUpdate(editingAnusthan.id, data);
              if (success) setEditingAnusthan(null);
            } else {
              const success = await onCreate(data);
              if (success) setShowCreateForm(false);
            }
          }}
          onCancel={() => { setEditingAnusthan(null); setShowCreateForm(false); }}
        />
      )}

      {/* Anusthans List */}
      <div style={{ padding: '16px' }}>
        {filteredAnusthans.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🪔</div>
            <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#BF360C', marginBottom: '8px' }}>No Anusthans Found</div>
            <div style={{ fontFamily: 'Lato, sans-serif', color: '#E64A19' }}>Add a new Anusthan to get started</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredAnusthans.map((anusthan) => (
              <div 
                key={anusthan.id} 
                style={{ 
                  border: '1px solid #FFCCBC', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  background: anusthan.is_active ? '#fff' : '#fafafa',
                  opacity: anusthan.is_active ? 1 : 0.7
                }}
              >
                {/* Header */}
                <div style={{ padding: '16px 20px', background: anusthan.is_active ? '#FFF8E1' : '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontSize: '2rem' }}>{anusthan.icon || '🪔'}</span>
                    <div>
                      <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#BF360C' }}>
                        {anusthan.name}
                      </div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666' }}>
                        {anusthan.devName} • {anusthan.deity}
                      </div>
                    </div>
                    {anusthan.featured && (
                      <span style={{ padding: '4px 10px', background: '#FFD700', color: '#333', fontSize: '0.7rem', fontWeight: '600', borderRadius: '4px' }}>FEATURED</span>
                    )}
                    {!anusthan.is_active && (
                      <span style={{ padding: '4px 10px', background: '#9E9E9E', color: '#fff', fontSize: '0.7rem', fontWeight: '600', borderRadius: '4px' }}>INACTIVE</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setEditingAnusthan(anusthan)}
                      style={{ padding: '8px 16px', background: '#1565C0', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => onDuplicate(anusthan.id)}
                      style={{ padding: '8px 16px', background: '#7B1FA2', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      📋 Copy
                    </button>
                    {anusthan.is_active && (
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to deactivate this Anusthan?')) {
                            onDelete(anusthan.id);
                          }
                        }}
                        style={{ padding: '8px 16px', background: '#D32F2F', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        🗑️ Deactivate
                      </button>
                    )}
                    {!anusthan.is_active && (
                      <button
                        onClick={() => onUpdate(anusthan.id, { is_active: true })}
                        style={{ padding: '8px 16px', background: '#4CAF50', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        ✓ Activate
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Content */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Date</div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontWeight: '500' }}>{anusthan.displayDate || formatDate(anusthan.date)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Tithi</div>
                      <div style={{ fontFamily: 'Lato, sans-serif' }}>{anusthan.tithiLabel || anusthan.tithi || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Packages</div>
                      <div style={{ fontFamily: 'Lato, sans-serif' }}>{(anusthan.packages || []).length} packages</div>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Short Description</div>
                    <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#333' }}>{anusthan.shortDesc || '-'}</div>
                  </div>
                  
                  {/* Tags */}
                  {anusthan.tags && anusthan.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {anusthan.tags.map((tag, idx) => (
                        <span key={idx} style={{ padding: '4px 10px', background: '#E3F2FD', color: '#1565C0', fontSize: '0.75rem', borderRadius: '4px' }}>{tag}</span>
                      ))}
                    </div>
                  )}
                  
                  {/* Packages Preview */}
                  {anusthan.packages && anusthan.packages.length > 0 && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                      <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', marginBottom: '8px' }}>Packages</div>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {anusthan.packages.map((pkg, idx) => (
                          <div key={idx} style={{ padding: '8px 12px', background: '#FFF3E0', border: '1px solid #FFCCBC', borderRadius: '4px' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#BF360C' }}>{pkg.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>₹{(pkg.price || 0).toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Anusthan Form Component
const AnusthanForm = ({ anusthan, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    featured: anusthan?.featured || false,
    target_goal: anusthan?.target_goal || '',
    planets: anusthan?.planets || '',
    icon: anusthan?.icon || '🪔',
    deity: anusthan?.deity || '',
    devName: anusthan?.devName || '',
    name: anusthan?.name || '',
    tithi: anusthan?.tithi || '',
    tithiLabel: anusthan?.tithiLabel || '',
    date: anusthan?.date || '',
    displayDate: anusthan?.displayDate || '',
    doshas: anusthan?.doshas || [],
    benefits: anusthan?.benefits || [],
    tags: anusthan?.tags || [],
    shortDesc: anusthan?.shortDesc || '',
    fullDesc: anusthan?.fullDesc || '',
    benefitsList: anusthan?.benefitsList || [],
    ritualSteps: anusthan?.ritualSteps || [],
    packages: anusthan?.packages || [],
    is_active: anusthan?.is_active !== undefined ? anusthan.is_active : true,
    image_url: anusthan?.image_url || ''
  });

  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value) => {
    // Convert comma-separated string to array
    const arr = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, [field]: arr }));
  };

  const handlePackageChange = (index, key, value) => {
    const newPackages = [...formData.packages];
    newPackages[index] = { ...newPackages[index], [key]: key === 'price' ? parseInt(value) || 0 : value };
    setFormData(prev => ({ ...prev, packages: newPackages }));
  };

  const addPackage = () => {
    setFormData(prev => ({
      ...prev,
      packages: [...prev.packages, { id: String.fromCharCode(97 + prev.packages.length), name: '', includes: '', price: 0 }]
    }));
  };

  const removePackage = (index) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
      return;
    }

    // Check if anusthan exists (need ID for upload)
    if (!anusthan?.id) {
      toast.error('Please save the Anusthan first before uploading an image');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/anusthans/${anusthan.id}/upload-image`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.image_url) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success('Image uploaded successfully');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter a name for the Anusthan');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', border: '1px solid #ddd', borderRadius: '4px' };
  const labelStyle = { display: 'block', fontSize: '0.75rem', color: '#666', textTransform: 'uppercase', marginBottom: '6px', fontWeight: '600' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '40px', zIndex: 1000, overflowY: 'auto' }}>
      <div style={{ background: '#fff', width: '90%', maxWidth: '900px', borderRadius: '8px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FBE9E7' }}>
          <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#BF360C', margin: 0 }}>
            {anusthan ? 'Edit Anusthan' : 'Create New Anusthan'}
          </h3>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: '#9E9E9E', color: '#fff', border: 'none', cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>

        {/* Section Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #eee', background: '#fafafa' }}>
          {['basic', 'content', 'packages'].map(section => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              style={{
                padding: '12px 24px',
                background: activeSection === section ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeSection === section ? '2px solid #FF5722' : '2px solid transparent',
                color: activeSection === section ? '#FF5722' : '#666',
                cursor: 'pointer',
                fontWeight: activeSection === section ? '600' : '400',
                textTransform: 'capitalize'
              }}
            >
              {section === 'basic' ? 'Basic Info' : section === 'content' ? 'Content' : 'Packages'}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {activeSection === 'basic' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Anusthan Name *</label>
                <input style={inputStyle} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="e.g. Ganesh Chaturthi Maha Puja" />
              </div>
              <div>
                <label style={labelStyle}>Sanskrit Name</label>
                <input style={inputStyle} value={formData.devName} onChange={(e) => handleChange('devName', e.target.value)} placeholder="e.g. गणेश चतुर्थी" />
              </div>
              <div>
                <label style={labelStyle}>Deity</label>
                <input style={inputStyle} value={formData.deity} onChange={(e) => handleChange('deity', e.target.value)} placeholder="e.g. Ganesh, Shiva, Durga" />
              </div>
              <div>
                <label style={labelStyle}>Icon Emoji</label>
                <input style={inputStyle} value={formData.icon} onChange={(e) => handleChange('icon', e.target.value)} placeholder="🪔" />
              </div>
              <div>
                <label style={labelStyle}>Date (YYYY-MM-DD)</label>
                <input style={inputStyle} type="date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Display Date</label>
                <input style={inputStyle} value={formData.displayDate} onChange={(e) => handleChange('displayDate', e.target.value)} placeholder="e.g. 27 August 2025" />
              </div>
              <div>
                <label style={labelStyle}>Tithi</label>
                <input style={inputStyle} value={formData.tithi} onChange={(e) => handleChange('tithi', e.target.value)} placeholder="e.g. Chaturthi" />
              </div>
              <div>
                <label style={labelStyle}>Tithi Label</label>
                <input style={inputStyle} value={formData.tithiLabel} onChange={(e) => handleChange('tithiLabel', e.target.value)} placeholder="e.g. Shukla Chaturthi" />
              </div>
              <div>
                <label style={labelStyle}>Tags (comma separated)</label>
                <input style={inputStyle} value={(formData.tags || []).join(', ')} onChange={(e) => handleArrayChange('tags', e.target.value)} placeholder="e.g. Obstacle Removal, New Beginnings" />
              </div>
              <div>
                <label style={labelStyle}>Benefits (comma separated)</label>
                <input style={inputStyle} value={(formData.benefits || []).join(', ')} onChange={(e) => handleArrayChange('benefits', e.target.value)} placeholder="e.g. Career, Wealth, Health" />
              </div>
              <div>
                <label style={labelStyle}>Doshas (comma separated)</label>
                <input style={inputStyle} value={(formData.doshas || []).join(', ')} onChange={(e) => handleArrayChange('doshas', e.target.value)} placeholder="e.g. Kaal Sarp, Mangal" />
              </div>
              
              {/* Goal-Based Anusthan Fields */}
              <div style={{ gridColumn: 'span 2', padding: '16px', border: '1px solid #D4AF37', borderRadius: '8px', background: 'rgba(212,175,55,0.05)' }}>
                <h4 style={{ margin: '0 0 16px', fontFamily: 'Cinzel, serif', fontSize: '0.95rem', color: '#8B4513' }}>⭐ Featured Goal-Based Settings</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>Target Goal</label>
                    <select 
                      style={inputStyle} 
                      value={formData.target_goal} 
                      onChange={(e) => handleChange('target_goal', e.target.value)}
                    >
                      <option value="">-- Select Target Goal --</option>
                      <option value="New Job / Promotion">New Job / Promotion</option>
                      <option value="Foreign Travel / Visa">Foreign Travel / Visa</option>
                      <option value="Fame & Recognition">Fame & Recognition</option>
                      <option value="Health & Long Life">Health & Long Life</option>
                      <option value="Business Abundance">Business Abundance</option>
                      <option value="Marriage / Relationship">Marriage / Relationship</option>
                      <option value="Education / Exams">Education / Exams</option>
                      <option value="Debt Relief">Debt Relief</option>
                      <option value="Legal Victory">Legal Victory</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Planet(s)</label>
                    <select 
                      style={inputStyle} 
                      value={formData.planets} 
                      onChange={(e) => handleChange('planets', e.target.value)}
                    >
                      <option value="">-- Select Planet(s) --</option>
                      <option value="Shani & Budh">Shani & Budh</option>
                      <option value="Rahu">Rahu</option>
                      <option value="Rahu & Budh">Rahu & Budh</option>
                      <option value="Shani & Ketu">Shani & Ketu</option>
                      <option value="Budh">Budh</option>
                      <option value="Surya">Surya</option>
                      <option value="Chandra">Chandra</option>
                      <option value="Mangal">Mangal</option>
                      <option value="Guru">Guru</option>
                      <option value="Shukra">Shukra</option>
                      <option value="Shani">Shani</option>
                      <option value="Ketu">Ketu</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', background: formData.featured ? '#D4AF37' : '#e0e0e0', borderRadius: '4px', color: formData.featured ? '#fff' : '#666' }}>
                  <input type="checkbox" checked={formData.featured} onChange={(e) => handleChange('featured', e.target.checked)} style={{ display: 'none' }} />
                  <span>⭐ {formData.featured ? 'Featured' : 'Not Featured'}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', background: formData.is_active ? '#4CAF50' : '#e0e0e0', borderRadius: '4px', color: formData.is_active ? '#fff' : '#666' }}>
                  <input type="checkbox" checked={formData.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} style={{ display: 'none' }} />
                  <span>{formData.is_active ? '✓ Active' : '✗ Inactive'}</span>
                </label>
              </div>
              {/* Image Upload Section */}
              <div style={{ gridColumn: 'span 2', padding: '16px', border: '1px dashed #FFCCBC', borderRadius: '8px', background: '#FFF8E1' }}>
                <label style={labelStyle}>Anusthan Image</label>
                {formData.image_url ? (
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <img 
                      src={formData.image_url.startsWith('/') ? `${API_URL}${formData.image_url}` : formData.image_url} 
                      alt="Anusthan" 
                      style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }} 
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.85rem', color: '#4CAF50', margin: '0 0 8px' }}>✓ Image uploaded</p>
                      <label style={{ display: 'inline-block', padding: '8px 16px', background: '#FF9800', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', borderRadius: '4px' }}>
                        Replace Image
                        <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage || !anusthan?.id} />
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    {!anusthan?.id ? (
                      <p style={{ fontSize: '0.85rem', color: '#F57C00', margin: '0 0 8px' }}>⚠️ Save the Anusthan first to upload an image</p>
                    ) : (
                      <>
                        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 8px' }}>Upload an image for this Anusthan (JPEG, PNG, WebP, GIF)</p>
                        <label style={{ display: 'inline-block', padding: '10px 20px', background: uploadingImage ? '#FFB74D' : '#FF5722', color: '#fff', cursor: uploadingImage ? 'not-allowed' : 'pointer', fontSize: '0.85rem', borderRadius: '4px' }}>
                          {uploadingImage ? '⏳ Uploading...' : '📷 Upload Image'}
                          <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                        </label>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'content' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Short Description</label>
                <textarea style={{ ...inputStyle, minHeight: '80px' }} value={formData.shortDesc} onChange={(e) => handleChange('shortDesc', e.target.value)} placeholder="Brief description shown in cards..." />
              </div>
              <div>
                <label style={labelStyle}>Full Description</label>
                <textarea style={{ ...inputStyle, minHeight: '120px' }} value={formData.fullDesc} onChange={(e) => handleChange('fullDesc', e.target.value)} placeholder="Detailed description shown on detail page..." />
              </div>
              <div>
                <label style={labelStyle}>Benefits List (one per line)</label>
                <textarea style={{ ...inputStyle, minHeight: '100px' }} value={(formData.benefitsList || []).join('\n')} onChange={(e) => handleChange('benefitsList', e.target.value.split('\n').filter(s => s.trim()))} placeholder="Removal of career obstacles&#10;Clarity for new ventures&#10;Relief from doshas" />
              </div>
              <div>
                <label style={labelStyle}>Ritual Steps (one per line)</label>
                <textarea style={{ ...inputStyle, minHeight: '100px' }} value={(formData.ritualSteps || []).join('\n')} onChange={(e) => handleChange('ritualSteps', e.target.value.split('\n').filter(s => s.trim()))} placeholder="Ganesh Avahan — invocation&#10;Shodashopachara Puja&#10;Your Sankalpa" />
              </div>
            </div>
          )}

          {activeSection === 'packages' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, color: '#BF360C' }}>Packages ({formData.packages.length})</h4>
                <button onClick={addPackage} style={{ padding: '8px 16px', background: '#4CAF50', color: '#fff', border: 'none', cursor: 'pointer' }}>
                  + Add Package
                </button>
              </div>
              {formData.packages.map((pkg, idx) => (
                <div key={idx} style={{ padding: '16px', border: '1px solid #FFCCBC', borderRadius: '8px', marginBottom: '12px', background: '#FFF8E1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: '600', color: '#BF360C' }}>Package {idx + 1}</span>
                    <button onClick={() => removePackage(idx)} style={{ padding: '4px 12px', background: '#D32F2F', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>Remove</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Package Name</label>
                      <input style={inputStyle} value={pkg.name || ''} onChange={(e) => handlePackageChange(idx, 'name', e.target.value)} placeholder="e.g. Puja Anushtha" />
                    </div>
                    <div>
                      <label style={labelStyle}>Price (₹)</label>
                      <input style={inputStyle} type="number" value={pkg.price || ''} onChange={(e) => handlePackageChange(idx, 'price', e.target.value)} placeholder="1100" />
                    </div>
                    <div>
                      <label style={labelStyle}>ID</label>
                      <input style={inputStyle} value={pkg.id || ''} onChange={(e) => handlePackageChange(idx, 'id', e.target.value)} placeholder="a" />
                    </div>
                    <div style={{ gridColumn: 'span 3' }}>
                      <label style={labelStyle}>Includes</label>
                      <input style={inputStyle} value={pkg.includes || ''} onChange={(e) => handlePackageChange(idx, 'includes', e.target.value)} placeholder="Full Vedic Puja + Sankalpa with your name" />
                    </div>
                  </div>
                </div>
              ))}
              {formData.packages.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999', background: '#fafafa', borderRadius: '8px' }}>
                  No packages added yet. Click "Add Package" to create one.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#fafafa' }}>
          <button onClick={onCancel} style={{ padding: '12px 24px', background: '#9E9E9E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '12px 24px', background: saving ? '#FF8A65' : '#FF5722', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}>
            {saving ? 'Saving...' : anusthan ? 'Update Anusthan' : 'Create Anusthan'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════
// REVENUE TAB COMPONENT
// ══════════════════════════════════════════

export const RevenueTab = ({ data, onRefresh }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    service_type: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    group_by: 'month'
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh(filters);
      toast.success('Revenue data refreshed');
    } catch (err) {
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    onRefresh(filters);
  };

  const formatCurrency = (amount) => {
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #FFE0B2' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #FFE0B2', background: '#FFF3E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#E65100', margin: 0 }}>Revenue Analytics</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#F57C00', margin: '4px 0 0' }}>
            Track revenue by service, customer, and date
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#FFB74D' : '#FF9800', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ padding: '16px 24px', background: '#FFF8E1', borderBottom: '1px solid #FFE0B2', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#E65100', marginBottom: '4px', textTransform: 'uppercase' }}>Service Type</label>
          <select 
            value={filters.service_type} 
            onChange={(e) => handleFilterChange('service_type', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #FFB74D', minWidth: '150px' }}
          >
            <option value="">All Services</option>
            <option value="astro_analysis">Astro Analysis</option>
            <option value="anusthan">Anusthan</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#E65100', marginBottom: '4px', textTransform: 'uppercase' }}>Customer ID</label>
          <input 
            type="text" 
            placeholder="e.g. SRI-C00001"
            value={filters.customer_id} 
            onChange={(e) => handleFilterChange('customer_id', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #FFB74D', minWidth: '140px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#E65100', marginBottom: '4px', textTransform: 'uppercase' }}>Start Date</label>
          <input 
            type="date" 
            value={filters.start_date} 
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #FFB74D' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#E65100', marginBottom: '4px', textTransform: 'uppercase' }}>End Date</label>
          <input 
            type="date" 
            value={filters.end_date} 
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #FFB74D' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#E65100', marginBottom: '4px', textTransform: 'uppercase' }}>Group By</label>
          <select 
            value={filters.group_by} 
            onChange={(e) => handleFilterChange('group_by', e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #FFB74D' }}
          >
            <option value="month">Month</option>
            <option value="day">Day</option>
            <option value="service">Service</option>
          </select>
        </div>
        <button 
          onClick={applyFilters}
          style={{ padding: '8px 20px', background: '#FF9800', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}
        >
          Apply Filters
        </button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '200px', padding: '20px', background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', color: '#fff', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>Total Revenue</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', marginTop: '8px' }}>{formatCurrency(data.total_revenue || 0)}</div>
            </div>
            <div style={{ flex: '1', minWidth: '200px', padding: '20px', background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', color: '#fff', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>Total Orders</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', marginTop: '8px' }}>{data.total_orders || 0}</div>
            </div>
            {data.service_summary && Object.entries(data.service_summary).map(([service, info]) => (
              <div key={service} style={{ flex: '1', minWidth: '200px', padding: '20px', background: service === 'astro_analysis' ? 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' : 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 100%)', color: '#fff', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>{service === 'astro_analysis' ? 'Astro Analysis' : 'Anusthan'}</div>
                <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.5rem', marginTop: '8px' }}>{formatCurrency(info.revenue || 0)}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>{info.count} orders</div>
              </div>
            ))}
          </div>

          {/* Grouped Data Table */}
          {data.grouped_data && data.grouped_data.length > 0 && (
            <div style={{ border: '1px solid #FFE0B2', borderRadius: '4px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#FFF3E0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', color: '#E65100', textTransform: 'uppercase' }}>
                      {filters.group_by === 'service' ? 'Service' : filters.group_by === 'day' ? 'Date' : 'Month'}
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.75rem', color: '#E65100', textTransform: 'uppercase' }}>Orders</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.75rem', color: '#E65100', textTransform: 'uppercase' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grouped_data.map((item, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #FFE0B2', background: idx % 2 === 0 ? '#fff' : '#FFFDE7' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                        {item.service_type || item.month || item.date || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>{item.count}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#2E7D32' }}>{formatCurrency(item.revenue || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!data.grouped_data || data.grouped_data.length === 0) && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              No revenue data found for the selected filters
            </div>
          )}
        </div>
      )}

      {!data && (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💰</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#E65100' }}>Loading Revenue Data...</div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// ANALYTICS TAB COMPONENT
// ══════════════════════════════════════════

export const AnalyticsTab = ({ data, onRefresh }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Analytics refreshed');
    } catch (err) {
      toast.error('Failed to refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #CFD8DC' }}>
      <div style={{ padding: '24px', borderBottom: '1px solid #CFD8DC', background: '#ECEFF1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#455A64', margin: 0 }}>Website Analytics</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#607D8B', margin: '4px 0 0' }}>
            Visitor statistics and user engagement
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', background: isRefreshing ? '#90A4AE' : '#607D8B', color: '#fff', border: 'none', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {data ? (
        <div style={{ padding: '24px' }}>
          {/* Summary Cards */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '200px', padding: '20px', background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)', color: '#fff', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>Total Visits</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', marginTop: '8px' }}>{data.total_visits || 0}</div>
            </div>
            <div style={{ flex: '1', minWidth: '200px', padding: '20px', background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', color: '#fff', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>Registered Users</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', marginTop: '8px' }}>{data.total_customers || 0}</div>
            </div>
            <div style={{ flex: '1', minWidth: '200px', padding: '20px', background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', color: '#fff', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.9 }}>Registered Not Ordered</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', marginTop: '8px' }}>{data.registered_not_ordered || 0}</div>
            </div>
          </div>

          {/* Page Visits */}
          {data.page_visits && Object.keys(data.page_visits).length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#455A64', marginBottom: '12px' }}>Page Visits</h3>
              <div style={{ border: '1px solid #CFD8DC', borderRadius: '4px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#ECEFF1' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', color: '#455A64', textTransform: 'uppercase' }}>Page</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.75rem', color: '#455A64', textTransform: 'uppercase' }}>Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.page_visits).sort((a, b) => b[1] - a[1]).map(([page, visits], idx) => (
                      <tr key={page} style={{ borderTop: '1px solid #CFD8DC', background: idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding: '12px 16px' }}>{page}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600' }}>{visits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Platform Distribution */}
          {data.platform_visits && Object.keys(data.platform_visits).length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#455A64', marginBottom: '12px' }}>Platform Distribution</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {Object.entries(data.platform_visits).map(([platform, count]) => (
                  <div key={platform} style={{ padding: '12px 20px', background: '#ECEFF1', borderRadius: '4px', border: '1px solid #CFD8DC' }}>
                    <div style={{ fontSize: '0.75rem', color: '#607D8B', textTransform: 'uppercase' }}>{platform}</div>
                    <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#455A64', marginTop: '4px' }}>{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Location Distribution */}
          {data.location_visits && Object.keys(data.location_visits).length > 0 && (
            <div>
              <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.1rem', color: '#455A64', marginBottom: '12px' }}>Location Distribution</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {Object.entries(data.location_visits).sort((a, b) => b[1] - a[1]).map(([location, count]) => (
                  <div key={location} style={{ padding: '12px 20px', background: '#E3F2FD', borderRadius: '4px', border: '1px solid #BBDEFB' }}>
                    <div style={{ fontSize: '0.75rem', color: '#1565C0', textTransform: 'uppercase' }}>{location}</div>
                    <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.3rem', color: '#0D47A1', marginTop: '4px' }}>{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.total_visits === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999', background: '#FAFAFA', borderRadius: '8px' }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📊</div>
              <div>No visitor data tracked yet. Analytics will populate as visitors browse the website.</div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📊</div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#607D8B' }}>Loading Analytics...</div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════
// ARCHIVE TAB COMPONENT
// ══════════════════════════════════════════

export const ArchiveTab = ({ archivedAnusthans, archivedAstroAnalysis, archivedBhojDaan, pastCustomers, missedRecommendations, onRefresh }) => {
  const [activeSubTab, setActiveSubTab] = useState('past-customers');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
      toast.success('Data refreshed successfully');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const thStyle = { 
    padding: '12px 14px', 
    textAlign: 'left', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.7rem', 
    letterSpacing: '0.1em', 
    textTransform: 'uppercase', 
    color: '#725b47', 
    fontWeight: '600',
    background: '#f5efe6'
  };
  
  const tdStyle = { 
    padding: '12px 14px', 
    fontFamily: 'Lato, sans-serif', 
    fontSize: '0.85rem', 
    color: '#2E221A', 
    verticalAlign: 'middle' 
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceDisplayName = (serviceType) => {
    switch (serviceType) {
      case 'astro_analysis': return 'Astro Analysis';
      case 'anusthan': return 'Anusthan';
      case 'bhoj_daan': return 'Bhoj & Daan';
      default: return serviceType || 'Unknown';
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d5c5' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', background: '#f5efe6', borderBottom: '1px solid #e0d5c5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#2E221A', margin: 0 }}>
            Archive
          </h3>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#725b47', margin: '8px 0 0' }}>
            Past Customers, Astro Analysis, Anusthans, Bhoj & Daan, Missed Recommendations
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ padding: '10px 20px', fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', background: isRefreshing ? '#f5f5f5' : 'transparent', color: '#8B4513', border: '1px solid #8B4513', cursor: isRefreshing ? 'not-allowed' : 'pointer', opacity: isRefreshing ? 0.7 : 1 }}
        >
          {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e0d5c5', background: '#faf8f5', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveSubTab('past-customers')}
          style={{
            padding: '14px 24px',
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.8rem',
            background: activeSubTab === 'past-customers' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'past-customers' ? '2px solid #4CAF50' : '2px solid transparent',
            color: activeSubTab === 'past-customers' ? '#4CAF50' : '#725b47',
            cursor: 'pointer',
            fontWeight: activeSubTab === 'past-customers' ? '600' : '400'
          }}
        >
          👥 Past Customers ({pastCustomers?.length || 0})
        </button>
        <button
          onClick={() => setActiveSubTab('anusthans')}
          style={{
            padding: '14px 24px',
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.8rem',
            background: activeSubTab === 'anusthans' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'anusthans' ? '2px solid #7B1FA2' : '2px solid transparent',
            color: activeSubTab === 'anusthans' ? '#7B1FA2' : '#725b47',
            cursor: 'pointer',
            fontWeight: activeSubTab === 'anusthans' ? '600' : '400'
          }}
        >
          🪔 Anusthans ({archivedAnusthans.length})
        </button>
        <button
          onClick={() => setActiveSubTab('astro')}
          style={{
            padding: '14px 24px',
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.8rem',
            background: activeSubTab === 'astro' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'astro' ? '2px solid #1565C0' : '2px solid transparent',
            color: activeSubTab === 'astro' ? '#1565C0' : '#725b47',
            cursor: 'pointer',
            fontWeight: activeSubTab === 'astro' ? '600' : '400'
          }}
        >
          ⭐ Astro Analysis ({archivedAstroAnalysis.length})
        </button>
        <button
          onClick={() => setActiveSubTab('bhoj-daan')}
          style={{
            padding: '14px 24px',
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.8rem',
            background: activeSubTab === 'bhoj-daan' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'bhoj-daan' ? '2px solid #00695C' : '2px solid transparent',
            color: activeSubTab === 'bhoj-daan' ? '#00695C' : '#725b47',
            cursor: 'pointer',
            fontWeight: activeSubTab === 'bhoj-daan' ? '600' : '400'
          }}
        >
          🙏 Bhoj & Daan ({archivedBhojDaan?.length || 0})
        </button>
        <button
          onClick={() => setActiveSubTab('missed-recommendations')}
          style={{
            padding: '14px 24px',
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.8rem',
            background: activeSubTab === 'missed-recommendations' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeSubTab === 'missed-recommendations' ? '2px solid #E65100' : '2px solid transparent',
            color: activeSubTab === 'missed-recommendations' ? '#E65100' : '#725b47',
            cursor: 'pointer',
            fontWeight: activeSubTab === 'missed-recommendations' ? '600' : '400'
          }}
        >
          ⚠️ Missed Recommendations ({missedRecommendations?.length || 0})
        </button>
      </div>

      {/* Past Customers */}
      {activeSubTab === 'past-customers' && (
        <div>
          {(!pastCustomers || pastCustomers.length === 0) ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👥</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#4CAF50', marginBottom: '8px' }}>No Past Customers</div>
              <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>Customers with completed orders will appear here</div>
            </div>
          ) : (
            <div style={{ padding: '16px' }}>
              {pastCustomers.map((customer, idx) => {
                const isExpanded = expandedCustomer === customer.customer_id;
                
                return (
                  <div key={idx} style={{ marginBottom: '12px', border: '1px solid #C8E6C9', borderRadius: '4px', overflow: 'hidden' }}>
                    {/* Customer Row */}
                    <div 
                      onClick={() => setExpandedCustomer(isExpanded ? null : customer.customer_id)}
                      style={{ 
                        padding: '16px 20px', 
                        background: '#E8F5E9', 
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'Cinzel, serif', color: '#2E7D32', fontWeight: '600' }}>
                          {customer.customer_id}
                        </span>
                        <span style={{ fontFamily: 'Lato, sans-serif', color: '#2E221A' }}>
                          {customer.customer_name}
                        </span>
                        <span style={{ fontFamily: 'Lato, sans-serif', color: '#666', fontSize: '0.85rem' }}>
                          📞 {customer.customer_phone}
                        </span>
                        <span style={{ fontFamily: 'Lato, sans-serif', color: '#666', fontSize: '0.85rem' }}>
                          ✉️ {customer.customer_email}
                        </span>
                        <span style={{ 
                          background: '#4CAF50', 
                          color: '#fff', 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          fontSize: '0.75rem' 
                        }}>
                          {customer.total_completed_orders} Completed Order(s)
                        </span>
                      </div>
                      <span style={{ color: '#2E7D32', fontSize: '1.2rem' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                    
                    {/* Expanded Orders */}
                    {isExpanded && (
                      <div style={{ padding: '16px', background: '#fff' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Order ID</th>
                              <th style={thStyle}>Order Date/Time</th>
                              <th style={thStyle}>Service</th>
                              <th style={thStyle}>Sub Service</th>
                              <th style={thStyle}>Package</th>
                              <th style={thStyle}>Price</th>
                              <th style={thStyle}>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customer.orders.map((order, oIdx) => (
                              <tr key={oIdx} style={{ borderBottom: '1px solid #e0d5c5', background: oIdx % 2 === 0 ? '#fff' : '#faf8f5' }}>
                                <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span></td>
                                <td style={tdStyle}>{formatDateTime(order.created_at)}</td>
                                <td style={tdStyle}><span style={{ color: order.service_type === 'astro_analysis' ? '#1565C0' : '#7B1FA2', fontWeight: '500' }}>{getServiceDisplayName(order.service_type)}</span></td>
                                <td style={tdStyle}>{order.service_subtype || '-'}</td>
                                <td style={tdStyle}>{order.package_name || '-'}</td>
                                <td style={tdStyle}>{order.amount > 0 ? `₹${(order.amount / 100).toLocaleString()}` : '-'}</td>
                                <td style={tdStyle}>
                                  {order.service_type === 'astro_analysis' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      {order.bracelet_config && order.bracelet_config.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                          {order.bracelet_config.filter(s => s.count > 0).map((stone, sIdx) => (
                                            <span key={sIdx} style={{ padding: '2px 6px', background: '#e3f2fd', fontSize: '0.65rem', color: '#1565C0' }}>
                                              {stone.stone}: {stone.count}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      {order.astro_analysis_file && (
                                        <a href={order.astro_analysis_file} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline', fontSize: '0.8rem' }}>
                                          📄 Download Analysis
                                        </a>
                                      )}
                                    </div>
                                  ) : (
                                    order.video_url ? (
                                      <a href={order.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#7B1FA2', textDecoration: 'underline', fontSize: '0.8rem' }}>
                                        🎥 Watch Video
                                      </a>
                                    ) : '-'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Anusthans Archive */}
      {activeSubTab === 'anusthans' && (
        <div>
          {archivedAnusthans.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🪔</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#7B1FA2', marginBottom: '8px' }}>No Archived Anusthans</div>
              <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>Completed Anusthans will appear here</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Customer ID</th>
                    <th style={thStyle}>Order ID</th>
                    <th style={thStyle}>Order Date/Time</th>
                    <th style={thStyle}>Mobile</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Anusthan Name</th>
                    <th style={thStyle}>Package</th>
                    <th style={thStyle}>Price</th>
                    <th style={thStyle}>Video URL</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedAnusthans.map((order, idx) => (
                    <tr key={order.order_id} style={{ borderBottom: '1px solid #e0d5c5', background: idx % 2 === 0 ? '#fff' : '#faf8f5' }}>
                      <td style={tdStyle}><span style={{ color: '#7B1FA2', fontWeight: '500' }}>{order.customer_id}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span></td>
                      <td style={tdStyle}>{formatDateTime(order.created_at)}</td>
                      <td style={tdStyle}>{order.customer_phone || '-'}</td>
                      <td style={tdStyle}>{order.customer_email || '-'}</td>
                      <td style={tdStyle}><span style={{ color: '#7B1FA2', fontWeight: '500' }}>{order.anusthan_name}</span></td>
                      <td style={tdStyle}>{order.package_name || '-'}</td>
                      <td style={tdStyle}>{order.package_price > 0 ? `₹${order.package_price}` : '-'}</td>
                      <td style={tdStyle}>
                        {order.anusthan_video_url ? (
                          <a href={order.anusthan_video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline' }}>
                            🎥 Watch
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Astro Analysis Archive */}
      {activeSubTab === 'astro' && (
        <div>
          {archivedAstroAnalysis.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⭐</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#1565C0', marginBottom: '8px' }}>No Archived Astro Analysis</div>
              <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>Completed Astro Analysis orders will appear here</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Customer ID</th>
                    <th style={thStyle}>Order ID</th>
                    <th style={thStyle}>Order Date/Time</th>
                    <th style={thStyle}>Mobile</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Bracelet Configuration</th>
                    <th style={thStyle}>Astro Analysis Document</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedAstroAnalysis.map((order, idx) => (
                    <tr key={order.order_id} style={{ borderBottom: '1px solid #e0d5c5', background: idx % 2 === 0 ? '#fff' : '#faf8f5' }}>
                      <td style={tdStyle}><span style={{ color: '#1565C0', fontWeight: '500' }}>{order.customer_id}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span></td>
                      <td style={tdStyle}>{formatDateTime(order.created_at)}</td>
                      <td style={tdStyle}>{order.customer_phone || '-'}</td>
                      <td style={tdStyle}>{order.customer_email || '-'}</td>
                      <td style={tdStyle}>
                        {order.bracelet_config && order.bracelet_config.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {order.bracelet_config.filter(s => s.count > 0).map((stone, sIdx) => (
                              <span key={sIdx} style={{ padding: '2px 6px', background: '#e3f2fd', fontSize: '0.7rem', color: '#1565C0' }}>
                                {stone.stone}: {stone.count}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td style={tdStyle}>
                        {order.astro_analysis_file ? (
                          <a href={order.astro_analysis_file} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline' }}>
                            📄 Download
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bhoj & Daan Archive */}
      {activeSubTab === 'bhoj-daan' && (
        <div>
          {(!archivedBhojDaan || archivedBhojDaan.length === 0) ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🙏</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#00695C', marginBottom: '8px' }}>No Archived Bhoj & Daan</div>
              <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>Completed Bhoj & Daan donations will appear here</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Customer ID</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Order ID</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Order Date/Time</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Ceremony Date</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Mobile</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Seva Types</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Amount</th>
                    <th style={{ ...thStyle, background: '#e0f2f1', color: '#004D40' }}>Video URL</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedBhojDaan.map((order, idx) => (
                    <tr key={order.order_id} style={{ borderBottom: '1px solid #b2dfdb', background: idx % 2 === 0 ? '#fff' : '#f5fcfb' }}>
                      <td style={tdStyle}><span style={{ color: '#00695C', fontWeight: '500' }}>{order.customer_id}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.order_id}</span></td>
                      <td style={tdStyle}>{formatDateTime(order.created_at)}</td>
                      <td style={tdStyle}>{order.ceremony_date || '-'}</td>
                      <td style={tdStyle}>{order.customer_phone || '-'}</td>
                      <td style={tdStyle}>
                        {order.sub_service_types && order.sub_service_types.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {order.sub_service_types.map((type, tIdx) => (
                              <span key={tIdx} style={{ padding: '2px 6px', background: '#e0f2f1', fontSize: '0.7rem', color: '#00695C' }}>
                                {type}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td style={tdStyle}>₹{((order.amount || 0) / 100).toLocaleString()}</td>
                      <td style={tdStyle}>
                        {order.video_url ? (
                          <a href={order.video_url} target="_blank" rel="noopener noreferrer" style={{ color: '#00695C', textDecoration: 'underline' }}>
                            📹 Watch
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Missed Recommended Anusthans */}
      {activeSubTab === 'missed-recommendations' && (
        <div>
          {(!missedRecommendations || missedRecommendations.length === 0) ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.2rem', color: '#4CAF50', marginBottom: '8px' }}>No Missed Recommendations</div>
              <div style={{ fontFamily: 'Lato, sans-serif', color: '#725b47' }}>All customers have participated in their recommended Anusthans</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Customer ID</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Order ID</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Order Date/Time</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Customer Name</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Mobile Number</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Recommended Anusthan</th>
                    <th style={{ ...thStyle, background: '#FFF3E0', color: '#E65100' }}>Proposed Ceremony Date</th>
                  </tr>
                </thead>
                <tbody>
                  {missedRecommendations.map((rec, idx) => (
                    <tr key={`${rec.order_id}-${idx}`} style={{ borderBottom: '1px solid #FFE0B2', background: idx % 2 === 0 ? '#fff' : '#FFFDE7' }}>
                      <td style={tdStyle}><span style={{ color: '#E65100', fontWeight: '500' }}>{rec.customer_id}</span></td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rec.order_id}</span></td>
                      <td style={tdStyle}>{formatDateTime(rec.created_at)}</td>
                      <td style={tdStyle}>{rec.customer_name || '-'}</td>
                      <td style={tdStyle}>{rec.customer_phone || '-'}</td>
                      <td style={tdStyle}><span style={{ color: '#7B1FA2', fontWeight: '500' }}>{rec.anusthan_name}</span></td>
                      <td style={tdStyle}>
                        <span style={{ padding: '4px 8px', background: '#FFEBEE', color: '#C62828', fontSize: '0.8rem', borderRadius: '4px' }}>
                          {rec.ceremony_date} (Missed)
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

